import { useFormik } from "formik";
import { get } from "lodash";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  clearPpeDetails,
  isPpeEndDateBeforeStart,
  resolveIsPep,
  shouldShowPpeDetails,
} from "screens/Private/Broker/Subscription/Detail/Templates/Acoris/Widgets/AdditionalInformation/ppeDeclaration";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ACORIS_ACTIVITY_STATUS,
  ACORIS_PROFESSIONS,
  DATE_FORMAT,
  FAMILY_SITUATION,
} from "utils/constants";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";
import Input from "../Components/Input";
import lightbulbIcon from "../Assets/icons/lightbulb.svg";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import InfoBox from "../Components/InfoBox";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import validation from "./validation";

function View({
  contract,
  ria,
  onSubmit,
  onBack,
  fields,
  terminationRequest,
  translate,
  project,
}) {
  const { t } = useTranslation();
  const { t: tProcesses } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const COUNTRIES = tProcesses("DETAIL.COUNTRIES", { returnObjects: true });
  const NATIONALITIES = tProcesses("DETAIL.NATIONALITIES", {
    returnObjects: true,
  });
  const DEFAULT_NATIONALITY = tProcesses("DETAIL.DEFAULTS.NATIONALITY");
  const ACORIS_TRANSLATIONS = tProcesses("B2C_ACORIS", {
    returnObjects: true,
  });

  const COUNTRY_OPTIONS = useMemo(
    () =>
      Array.isArray(COUNTRIES)
        ? COUNTRIES.map((country) => ({
            value: country,
            label: country,
          }))
        : [],
    [COUNTRIES],
  );

  const NATIONALITY_OPTIONS = useMemo(
    () =>
      Array.isArray(NATIONALITIES)
        ? NATIONALITIES.map((nationality) => ({
            value: nationality,
            label: nationality,
          }))
        : [],
    [NATIONALITIES],
  );

  const STATUS_OPTIONS = useMemo(
    () =>
      Object.entries(ACORIS_ACTIVITY_STATUS).map(([key, value]) => ({
        value,
        label:
          ACORIS_TRANSLATIONS.ACTIVITY_STATUS?.[key] ||
          translate?.STATUS_OPTIONS?.[key] ||
          value,
      })),
    [ACORIS_TRANSLATIONS.ACTIVITY_STATUS, translate],
  );

  const FAMILY_STATUS_OPTIONS = useMemo(
    () => [
      {
        value: FAMILY_SITUATION.SINGLE,
        label: translate?.FAMILY_STATUS_OPTIONS?.SINGLE,
      },
      {
        value: FAMILY_SITUATION.MARRIED,
        label: translate?.FAMILY_STATUS_OPTIONS?.MARRIED,
      },
      {
        value: FAMILY_SITUATION.PACS,
        label: translate?.FAMILY_STATUS_OPTIONS?.PACS,
      },
      {
        value: FAMILY_SITUATION.DIVORCED,
        label: translate?.FAMILY_STATUS_OPTIONS?.DIVORCED,
      },
      {
        value: FAMILY_SITUATION.WIDOWED,
        label: translate?.FAMILY_STATUS_OPTIONS?.WIDOWED,
      },
      {
        value: FAMILY_SITUATION.COHABITING,
        label: translate?.FAMILY_STATUS_OPTIONS?.COHABITING,
      },
    ],
    [translate],
  );

  const PROFESSION_OPTIONS = useMemo(
    () =>
      Object.entries(ACORIS_PROFESSIONS).map(([key, value]) => ({
        value,
        label:
          ACORIS_TRANSLATIONS.PROFESSIONS?.[key] ||
          translate?.PROFESSION_OPTIONS?.[key] ||
          value,
      })),
    [ACORIS_TRANSLATIONS.PROFESSIONS, translate],
  );

  const { submitForm, values, setFieldValue, validateForm, ...formProps } =
    useFormik({
      initialValues: {
        contract: {
          ...contract,
        },
        ria: ria || false,
        fields: {
          ...fields,
          familyStatus: fields?.familyStatus || null,
          birthCountry: fields?.birthCountry || "France",
          birthCity:
            fields?.birthCity ||
            get(project, "contact.telecoms", []).find(
              (telecom) => telecom.system === "BIRTH_CITY",
            )?.value ||
            "",
          birthDepartment: fields?.birthDepartment || null,
          nationality:
            fields?.nationality ||
            get(project, "contact.nationality") ||
            DEFAULT_NATIONALITY,
          activityStatus: fields?.activityStatus || null,
          profession: fields?.profession || null,
        },
        contact: {
          politicallyExposedPerson: resolveIsPep(
            get(project, "contact.politicallyExposedPerson"),
            fields?.isPep,
            fields?.isPPE,
          ),
          fields: {
            ppeFunction: get(project, "contact.fields.ppeFunction") || "",
            ppeStartDate: get(project, "contact.fields.ppeStartDate") || null,
            ppeEndDate: get(project, "contact.fields.ppeEndDate") || null,
          },
        },
        terminationRequest: terminationRequest || null,
      },
      onSubmit: onSubmit,
      enableReinitialize: true,
      validateOnMount: true,
      validationSchema: validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      ),
    });

  useEffect(() => {
    validateForm();
  }, [contract, fields, ria, terminationRequest, validateForm]);

  return (
    <Layout>
      <Header
        title={translate?.HEADER?.TITLE}
        highlight={translate?.HEADER?.HIGHLIGHT}
        subtitle={translate?.HEADER?.SUBTITLE}
      />
      <div className="acoris-form acoris-effective-date">
        <div className="acoris-form__container">
          <div className="acoris-effective-date__identity">
            <InfoBox
              className="acoris-form__info-banner"
              icon={lightbulbIcon}
              variant="info"
              text={translate?.INFO_BANNER?.TEXT}
            />
            <div className="acoris-effective-date__fields">
              <div className="acoris-form__row">
                <Select
                  label={translate?.FORM?.BIRTH_COUNTRY?.LABEL}
                  placeholder={translate?.FORM?.BIRTH_COUNTRY?.PLACEHOLDER}
                  options={COUNTRY_OPTIONS}
                  value={get(values, "fields.birthCountry") || undefined}
                  showSearch
                  {...bindInputProps({
                    name: "fields.birthCountry",
                    values,
                    ...formProps,
                  })}
                  onChange={(value) => {
                    setFieldValue("fields.birthCountry", value);
                    if (value !== "France") {
                      setFieldValue("fields.birthDepartment", "Autre/Etranger");
                    } else {
                      setFieldValue("fields.birthDepartment", null);
                    }
                  }}
                />
                <Input
                  label={translate?.FORM?.BIRTH_CITY?.LABEL}
                  placeholder={translate?.FORM?.BIRTH_CITY?.PLACEHOLDER}
                  {...bindInputProps({
                    name: "fields.birthCity",
                    values,
                    ...formProps,
                  })}
                />
              </div>
              <div className="acoris-form__row acoris-form__row--single">
                <Select
                  label={translate?.FORM?.NATIONALITY?.LABEL}
                  placeholder={translate?.FORM?.NATIONALITY?.PLACEHOLDER}
                  options={NATIONALITY_OPTIONS}
                  value={get(values, "fields.nationality") || undefined}
                  showSearch
                  {...bindInputProps({
                    name: "fields.nationality",
                    values,
                    ...formProps,
                  })}
                />
              </div>
              <div className="acoris-form__row">
                <Select
                  label={translate?.FORM?.FAMILY_STATUS?.LABEL}
                  placeholder={translate?.FORM?.FAMILY_STATUS?.PLACEHOLDER}
                  options={FAMILY_STATUS_OPTIONS}
                  value={get(values, "fields.familyStatus") || undefined}
                  {...bindInputProps({
                    name: "fields.familyStatus",
                    values,
                    ...formProps,
                  })}
                />
                <Select
                  label={translate?.FORM?.STATUS?.LABEL}
                  placeholder={translate?.FORM?.STATUS?.PLACEHOLDER}
                  options={STATUS_OPTIONS}
                  value={get(values, "fields.activityStatus") || undefined}
                  {...bindInputProps({
                    name: "fields.activityStatus",
                    values,
                    ...formProps,
                  })}
                />
              </div>
              <div className="acoris-form__row acoris-form__row--single">
                <Select
                  label={translate?.FORM?.PROFESSION?.LABEL}
                  placeholder={translate?.FORM?.PROFESSION?.PLACEHOLDER}
                  options={PROFESSION_OPTIONS}
                  {...bindInputProps({
                    name: "fields.profession",
                    values,
                    setFieldValue,
                    ...formProps,
                  })}
                />
              </div>
              <div className="acoris-form__row acoris-form__row--single">
                <Input
                  type="radio"
                  label={translate?.FORM?.PPE?.QUESTION}
                  values={[
                    {
                      label: translate?.FORM?.PPE?.YES,
                      value: true,
                    },
                    {
                      label: translate?.FORM?.PPE?.NO,
                      value: false,
                    },
                  ]}
                  value={get(values, "contact.politicallyExposedPerson")}
                  {...bindInputProps({
                    name: "contact.politicallyExposedPerson",
                    values,
                    ...formProps,
                  })}
                  onChange={(value) => {
                    setFieldValue("contact.politicallyExposedPerson", value);
                    if (!shouldShowPpeDetails(value)) {
                      const cleared = clearPpeDetails();
                      setFieldValue(
                        "contact.fields.ppeFunction",
                        cleared.ppeFunction,
                      );
                      setFieldValue(
                        "contact.fields.ppeStartDate",
                        cleared.ppeStartDate,
                      );
                      setFieldValue(
                        "contact.fields.ppeEndDate",
                        cleared.ppeEndDate,
                      );
                    }
                  }}
                />
              </div>
              {shouldShowPpeDetails(
                get(values, "contact.politicallyExposedPerson"),
              ) && (
                <>
                  <div className="acoris-form__row acoris-form__row--single">
                    <Input
                      required
                      label={translate?.FORM?.PPE?.FUNCTION}
                      {...bindInputProps({
                        name: "contact.fields.ppeFunction",
                        values,
                        ...formProps,
                      })}
                    />
                  </div>
                  <div className="acoris-form__row">
                    <Input
                      type="date"
                      required
                      format={DATE_FORMAT}
                      label={translate?.FORM?.PPE?.START_DATE}
                      disabledDate={(current) =>
                        isPpeEndDateBeforeStart(
                          current,
                          get(values, "contact.fields.ppeEndDate"),
                        )
                      }
                      {...bindDateInputProps({
                        name: "contact.fields.ppeStartDate",
                        values,
                        setFieldValue,
                        ...formProps,
                      })}
                    />
                    <Input
                      type="date"
                      format={DATE_FORMAT}
                      optional
                      label={translate?.FORM?.PPE?.END_DATE}
                      disabledDate={(current) =>
                        isPpeEndDateBeforeStart(
                          get(values, "contact.fields.ppeStartDate"),
                          current,
                        )
                      }
                      {...bindDateInputProps({
                        name: "contact.fields.ppeEndDate",
                        values,
                        setFieldValue,
                        ...formProps,
                      })}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
          <ActionButtons
            disabled={!formProps.isValid}
            onNext={submitForm}
            onBack={onBack}
            nextText={translate?.BUTTON?.CONTINUE}
            backText={translate?.BUTTON?.BACK}
          />
        </div>
      </div>
    </Layout>
  );
}

export default View;
